// ---------------------------------------------------------------------------
// friends tab — list, detail, and add-friend sub-views
// ---------------------------------------------------------------------------

import { Assets, Container, Graphics, Rectangle, Sprite, Text, Texture } from "pixi.js";
import type { DocumentId } from "@automerge/automerge-repo";
import { log } from "@freqhole/reliquary/utils";
import { CanvasBinStore } from "../../../src/canvas/canvas-bin-doc";
import { ProfileStore } from "../../../src/canvas/profile-doc";
import {
  getHubAdminTransport,
  gossipFriendRequestsNow,
  isKnownHubNodeId,
  isOnline as bridgeIsOnline,
  isProtocolReady as bridgeIsProtocolReady,
  onOnlineChange,
  probePeer,
  requestProfile,
  sendFriendRequest,
} from "../../../src/p2p/friendz-bridge";
import { createHubAdminClient } from "../../../src/p2p/hub-admin-client";
import { registerSocialBridge } from "../../../src/dev/test-bridge-registry";
import { createSkeinInput, type SkeinInputHandle } from "../../../src/widgets/skein-input";
import {
  createProfileCanvasBinWidget,
  PROFILE_CANVAS_BIN_HEIGHT,
  type ProfileCanvasBinController,
} from "./canvas-bin";
import { renderAvatar } from "./avatar-renderer";
import {
  ACCENT,
  BG,
  BORDER,
  BUTTON_GAP,
  BUTTON_HEIGHT,
  BUTTON_RADIUS,
  FIELD_BG,
  FIELD_BORDER,
  FIELD_GAP,
  FIELD_HEIGHT,
  FONT,
  LABEL_COLOR,
  LABEL_SIZE,
  MUTED_TEXT,
  OFFLINE_COLOR,
  ONLINE_COLOR,
  ONLINE_DOT_SIZE,
  OPTION_FONT_SIZE,
  OPTION_PILL_GAP,
  OPTION_PILL_HEIGHT,
  OPTION_PILL_RADIUS,
  PADDING_X,
  REJECT_COLOR,
  RESOLUTION,
  DRAG_HOLD_MS,
  DRAG_ROW_DIMMED_ALPHA,
  DRAG_THRESHOLD,
  DROP_ZONE_ACTIVE_BG,
  DROP_ZONE_BG,
  DROP_ZONE_BORDER,
  DROP_ZONE_FONT_SIZE,
  DROP_ZONE_HEIGHT,
  GHOST_ALPHA,
  GROUP_LINE_INSET,
  GROUP_LINE_WIDTH,
  HEADER_HOLD_MS,
  ROW_ALT_BG,
  ROW_AVATAR_SIZE,
  ROW_HEIGHT,
  ROW_HEIGHT_BIO,
  ROW_NAME_SIZE,
  ROW_PADDING_X,
  ROW_SUB_SIZE,
  SCROLL_SPEED,
  TAB_FONT_SIZE,
  TEXT_COLOR,
  TEXT_SIZE,
} from "./constants";
import {
  bestBio,
  buildFriendRowItems,
  colorForName,
  friendDisplayName,
  friendDisplayNameFull,
  friendHasPendingOutboundRequest,
  generateUniqueGroupName,
  HUB_GROUP_KEY,
  HUB_GROUP_LABEL,
  isValidNodeId,
  truncate,
} from "./helpers";
import { mountHubProfilePanel, type HubProfilePanelHandle } from "./hub-profile-panel";
import type { FriendEntry, FriendGroup } from "./schema";
import type { TabContext, TabController } from "./types";

// ---------------------------------------------------------------------------
// detail view local constants
// ---------------------------------------------------------------------------

const DETAIL_AVATAR_SIZE = 48;
const DETAIL_NAME_SIZE = 16;
const DETAIL_BIO_SIZE = 11;
const DETAIL_NODEID_SIZE = 10;
const DETAIL_BTN_HEIGHT = 32;
const DETAIL_BTN_RADIUS = 4;
const COPY_FEEDBACK_MS = 1500;

// ---------------------------------------------------------------------------
// collapsed groups — module-level singleton state
// ---------------------------------------------------------------------------

const collapsedGroups = new Set<string>();
const headerLastTapTime = new Map<string, number>();

// ---------------------------------------------------------------------------
// factory
// ---------------------------------------------------------------------------

export function createFriendsTab(ctx: TabContext): TabController {
  const container = new Container();
  container.eventMode = "static";

  let currentWidth = 0;
  let currentHeight = 0;

  // internal sub-view mode (managed entirely by this tab)
  let viewMode: "list" | "detail" | "add" | "hubProfile" = "list";

  // selected friend for detail view
  let selectedFriendId: string | null = null;

  // hub-profile-panel state (docs/hub-and-profile-plan.md section 5/8 step 7)
  let hubProfileHandle: HubProfilePanelHandle | null = null;
  let hubProfileHubNodeId: string | null = null;

  // a friend's read-only canvas-bin section in the detail view (see
  // rebuildDetailView() below) — best-effort, mounted asynchronously once
  // the friend's profile+bin docs are reachable.
  let friendCanvasBinController: ProfileCanvasBinController | null = null;

  // live profile refresh (username/bio/avatar) for the currently-viewed
  // friend — see rebuildDetailView()'s "live profile refresh" section.
  // `profileRefreshRequestedFor` guards against re-sending a profile-request
  // on every incidental re-render while the detail view stays open (doc
  // changes elsewhere, online-status flips, resize, etc) — cleared by
  // layout() whenever the view leaves "detail", so re-opening the same
  // friend later fetches again. `profileRefreshPendingFor` drives the small
  // "refreshing profile..." indicator and is cleared once the request
  // settles (success or failure) or a different friend is opened.
  let profileRefreshRequestedFor: string | null = null;
  let profileRefreshPendingFor: string | null = null;

  // active presence probe (one-shot heartbeat) for the currently-viewed
  // friend — see rebuildDetailView()'s "presence probe" section. re-checks
  // online status once per detail-view visit rather than relying solely on
  // the last passively-received heartbeat, which can be stale by up to
  // HEARTBEAT_TIMEOUT_MS. cleared by layout() alongside
  // profileRefreshRequestedFor when leaving "detail".
  let presenceProbeRequestedFor: string | null = null;

  // scroll state for the list view
  let scrollY = 0;

  // detail view editing state
  let editingAlias = false;
  let aliasInputHandle: SkeinInputHandle | null = null;
  let editingNewGroup = false;
  let groupInputHandle: SkeinInputHandle | null = null;

  // rendered hub-node-id text in the detail view (docs/hub-and-profile-
  // plan.md section 10.3) — kept for the getFriendDetailNodeIdText() test
  // hook, so tests can assert on the actual rendered content rather than
  // just the underlying FriendEntry data.
  let detailNodeIdTextRef: Text | null = null;

  // drag-and-drop state
  let dragState: null | {
    friendId: string;
    friendGroup: string;
    startX: number;
    startY: number;
    holdTimer: ReturnType<typeof setTimeout> | null;
    isDragging: boolean;
    ghost: Container | null;
  } = null;

  // inline group rename state
  let editingGroupName: string | null = null;
  let groupRenameHandle: SkeinInputHandle | null = null;

  // track group header positions for drop hit testing during drag
  let groupHeaderHitAreas: { group: string; y: number; height: number }[] = [];

  // ---------------------------------------------------------------------------
  // friend list area (scrollable, masked)
  // ---------------------------------------------------------------------------

  const listContainer = new Container();
  listContainer.eventMode = "static";
  container.addChild(listContainer);

  const listMask = new Graphics();
  container.addChild(listMask);
  listContainer.mask = listMask;

  // inner container for friend rows — positioned by scrollY
  const listInner = new Container();
  listInner.eventMode = "static";
  listContainer.addChild(listInner);

  // placeholder text for empty state
  const emptyText = new Text({
    text: "no friends yet",
    style: {
      fontFamily: FONT,
      fontSize: 11,
      fill: MUTED_TEXT,
    },
    resolution: RESOLUTION,
  });
  emptyText.eventMode = "none";
  container.addChild(emptyText);

  // -------------------------------------------------------------------------
  // drop zone bar — fixed at top of list, visible only during drag
  // -------------------------------------------------------------------------

  const dropZoneBar = new Container();
  dropZoneBar.visible = false;
  dropZoneBar.eventMode = "static";
  container.addChild(dropZoneBar);

  const dzNewGroupBg = new Graphics();
  dzNewGroupBg.eventMode = "static";
  dzNewGroupBg.cursor = "copy";
  dropZoneBar.addChild(dzNewGroupBg);

  const dzNewGroupLabel = new Text({
    text: "+ new group",
    style: { fontFamily: FONT, fontSize: DROP_ZONE_FONT_SIZE, fill: TEXT_COLOR },
    resolution: RESOLUTION,
  });
  dzNewGroupLabel.eventMode = "none";
  dropZoneBar.addChild(dzNewGroupLabel);

  const dzRemoveBg = new Graphics();
  dzRemoveBg.eventMode = "static";
  dzRemoveBg.cursor = "pointer";
  dropZoneBar.addChild(dzRemoveBg);

  const dzRemoveLabel = new Text({
    text: "remove from group",
    style: { fontFamily: FONT, fontSize: DROP_ZONE_FONT_SIZE, fill: TEXT_COLOR },
    resolution: RESOLUTION,
  });
  dzRemoveLabel.eventMode = "none";
  dropZoneBar.addChild(dzRemoveLabel);

  // -------------------------------------------------------------------------
  // drag-and-drop management
  // -------------------------------------------------------------------------

  const showDropZones = (friendGroup: string) => {
    dropZoneBar.visible = true;
    dzRemoveBg.visible = friendGroup !== "";
    dzRemoveLabel.visible = friendGroup !== "";
  };

  const hideDropZones = () => {
    dropZoneBar.visible = false;
  };

  const layoutDropZones = (contentW: number, topY: number) => {
    dropZoneBar.x = 0;
    dropZoneBar.y = topY;

    const halfW = Math.floor(contentW / 2);

    // left zone: + new group
    dzNewGroupBg.clear();
    dzNewGroupBg.roundRect(0, 0, halfW - 2, DROP_ZONE_HEIGHT, 4);
    dzNewGroupBg.fill({ color: DROP_ZONE_BG });
    dzNewGroupBg.stroke({ color: DROP_ZONE_BORDER, width: 1 });
    dzNewGroupBg.hitArea = new Rectangle(0, 0, halfW - 2, DROP_ZONE_HEIGHT);
    dzNewGroupLabel.x = (halfW - 2 - dzNewGroupLabel.width) / 2;
    dzNewGroupLabel.y = (DROP_ZONE_HEIGHT - DROP_ZONE_FONT_SIZE) / 2;

    // right zone: remove from group
    dzRemoveBg.clear();
    dzRemoveBg.roundRect(halfW + 2, 0, halfW - 2, DROP_ZONE_HEIGHT, 4);
    dzRemoveBg.fill({ color: DROP_ZONE_BG });
    dzRemoveBg.stroke({ color: DROP_ZONE_BORDER, width: 1 });
    dzRemoveBg.hitArea = new Rectangle(halfW + 2, 0, halfW - 2, DROP_ZONE_HEIGHT);
    dzRemoveLabel.x = halfW + 2 + (halfW - 2 - dzRemoveLabel.width) / 2;
    dzRemoveLabel.y = (DROP_ZONE_HEIGHT - DROP_ZONE_FONT_SIZE) / 2;
  };

  /** start a drag operation — creates ghost, shows drop zones */
  const startDrag = (friendId: string, friendGroup: string, globalX: number, globalY: number) => {
    if (!dragState) return;
    dragState.isDragging = true;

    const friend = ctx.doc.current.friends.find((f) => f.id === friendId);
    if (!friend) return;

    const ghost = new Container();
    ghost.eventMode = "none";
    ghost.alpha = GHOST_ALPHA;

    const ghostBg = new Graphics();
    ghostBg.roundRect(0, 0, currentWidth * 0.7, ROW_HEIGHT, 6);
    ghostBg.fill({ color: 0x2a2a3e });
    ghost.addChild(ghostBg);

    const ghostText = new Text({
      text: friendDisplayName(friend),
      style: { fontFamily: FONT, fontSize: ROW_NAME_SIZE, fontWeight: "bold", fill: TEXT_COLOR },
      resolution: RESOLUTION,
    });
    ghostText.x = 10;
    ghostText.y = (ROW_HEIGHT - ROW_NAME_SIZE) / 2;
    ghost.addChild(ghostText);

    // position ghost centered on pointer
    const localPos = container.toLocal({ x: globalX, y: globalY });
    ghost.x = localPos.x - ghost.width / 2;
    ghost.y = localPos.y - ghost.height / 2;

    container.addChild(ghost);
    dragState.ghost = ghost;

    showDropZones(friendGroup);
    layoutDropZones(currentWidth, 0);

    // force rebuild to dim the dragged row and shift list below drop zones
    layout(currentWidth, currentHeight);
  };

  /** update drag position and highlight hovered drop targets */
  const updateDrag = (globalX: number, globalY: number) => {
    if (!dragState?.isDragging || !dragState.ghost) return;

    const localPos = container.toLocal({ x: globalX, y: globalY });
    dragState.ghost.x = localPos.x - dragState.ghost.width / 2;
    dragState.ghost.y = localPos.y - dragState.ghost.height / 2;

    // check hover over drop zone bar
    const dzY = dropZoneBar.y;
    const inDropBar = localPos.y >= dzY && localPos.y <= dzY + DROP_ZONE_HEIGHT;
    const halfW = Math.floor(currentWidth / 2);

    // highlight new-group zone
    dzNewGroupBg.clear();
    const newGroupHover = inDropBar && localPos.x < halfW;
    dzNewGroupBg.roundRect(0, 0, halfW - 2, DROP_ZONE_HEIGHT, 4);
    dzNewGroupBg.fill({ color: newGroupHover ? DROP_ZONE_ACTIVE_BG : DROP_ZONE_BG });
    dzNewGroupBg.stroke({ color: DROP_ZONE_BORDER, width: 1 });

    // highlight remove zone
    if (dzRemoveBg.visible) {
      dzRemoveBg.clear();
      const removeHover = inDropBar && localPos.x >= halfW;
      dzRemoveBg.roundRect(halfW + 2, 0, halfW - 2, DROP_ZONE_HEIGHT, 4);
      dzRemoveBg.fill({ color: removeHover ? DROP_ZONE_ACTIVE_BG : DROP_ZONE_BG });
      dzRemoveBg.stroke({ color: DROP_ZONE_BORDER, width: 1 });
    }
  };

  /** end drag — evaluate drop target and perform mutation */
  const endDrag = (globalX: number, globalY: number): "tap" | "dropped" | "cancelled" => {
    if (!dragState) return "cancelled";

    const friendId = dragState.friendId;
    const wasDragging = dragState.isDragging;

    // clean up
    if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
    if (dragState.ghost) {
      dragState.ghost.destroy({ children: true });
    }
    dragState = null;
    hideDropZones();

    if (!wasDragging) {
      return "tap";
    }

    // evaluate drop target
    const localPos = container.toLocal({ x: globalX, y: globalY });
    const dzY = dropZoneBar.y;
    const inDropBar = localPos.y >= dzY && localPos.y <= dzY + DROP_ZONE_HEIGHT;
    const halfW = Math.floor(currentWidth / 2);

    if (inDropBar && localPos.x < halfW) {
      // dropped on "+ new group"
      ctx.doc.change((draft) => {
        const name = generateUniqueGroupName(draft.groups);
        draft.groups.push({ name, createdAt: new Date().toISOString() });
        const f = draft.friends.find((fr) => fr.id === friendId);
        if (f) f.group = name;
      });
      // enter inline rename on the new group after re-render
      const updatedFriend = ctx.doc.current.friends.find((f) => f.id === friendId);
      if (updatedFriend && updatedFriend.group) {
        editingGroupName = updatedFriend.group;
      }
      layout(currentWidth, currentHeight);
      return "dropped";
    }

    if (inDropBar && localPos.x >= halfW) {
      // dropped on "remove from group"
      ctx.doc.change((draft) => {
        const f = draft.friends.find((fr) => fr.id === friendId);
        if (f) f.group = "";
      });
      layout(currentWidth, currentHeight);
      return "dropped";
    }

    // check group headers — adjust for scroll and drop zone offset
    const listTopOffset = DROP_ZONE_HEIGHT + 4;
    const scrollAdjustedY = localPos.y - listTopOffset + scrollY;
    for (const hit of groupHeaderHitAreas) {
      if (scrollAdjustedY >= hit.y && scrollAdjustedY <= hit.y + hit.height) {
        ctx.doc.change((draft) => {
          const f = draft.friends.find((fr) => fr.id === friendId);
          if (f) f.group = hit.group;
        });
        layout(currentWidth, currentHeight);
        return "dropped";
      }
    }

    // dropped nowhere meaningful — cancel
    layout(currentWidth, currentHeight);
    return "cancelled";
  };

  const cancelDrag = () => {
    if (!dragState) return;
    if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
    if (dragState.ghost) {
      dragState.ghost.destroy({ children: true });
    }
    dragState = null;
    hideDropZones();
    layout(currentWidth, currentHeight);
  };

  // -------------------------------------------------------------------------
  // global safety net — any pointer/mouse/touch release clears drag state
  // -------------------------------------------------------------------------

  const globalRelease = (ev: PointerEvent | MouseEvent | TouchEvent) => {
    if (!dragState) return;
    // try to get coordinates for drop evaluation
    let gx = 0;
    let gy = 0;
    if ("clientX" in ev) {
      gx = ev.clientX;
      gy = ev.clientY;
    } else if ("changedTouches" in ev && ev.changedTouches.length > 0) {
      gx = ev.changedTouches[0].clientX;
      gy = ev.changedTouches[0].clientY;
    }
    if (dragState.isDragging) {
      endDrag(gx, gy);
    } else {
      // hold timer started but never became a drag — just clean up
      if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
      dragState = null;
    }
  };

  window.addEventListener("pointerup", globalRelease, true);
  window.addEventListener("mouseup", globalRelease, true);
  window.addEventListener("touchend", globalRelease, true);
  const globalCancel = () => cancelDrag();
  window.addEventListener("pointercancel", globalCancel, true);
  window.addEventListener("touchcancel", globalCancel, true);

  // container-level fallback for pixi event system
  container.on("pointerup", (e) => {
    if (!dragState) return;
    const gx = e.globalX ?? (e as any).global?.x ?? 0;
    const gy = e.globalY ?? (e as any).global?.y ?? 0;
    if (dragState.isDragging) {
      endDrag(gx, gy);
    } else {
      if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
      dragState = null;
    }
  });

  container.on("pointerupoutside", (e) => {
    if (!dragState) return;
    const gx = e.globalX ?? (e as any).global?.x ?? 0;
    const gy = e.globalY ?? (e as any).global?.y ?? 0;
    if (dragState.isDragging) {
      endDrag(gx, gy);
    } else {
      if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
      dragState = null;
    }
  });

  // scroll handler on the list container — only capture when content overflows
  listContainer.on("wheel", (e: WheelEvent) => {
    const canScroll = totalListHeight > listAreaHeight;
    if (!canScroll) return; // let the event pass through to the canvas viewport

    e.stopPropagation();
    // claim the native event so the viewport doesn't also pan
    if ((e as any).nativeEvent) (e as any).nativeEvent._skeinWidgetScroll = true;
    scrollY += e.deltaY > 0 ? SCROLL_SPEED : -SCROLL_SPEED;
    clampScroll();
    positionListInner();
  });

  let listAreaHeight = 0;
  let totalListHeight = 0;

  const clampScroll = () => {
    const maxScroll = Math.max(0, totalListHeight - listAreaHeight);
    scrollY = Math.max(0, Math.min(scrollY, maxScroll));
  };

  const positionListInner = () => {
    listInner.y = -scrollY;
  };

  // ---------------------------------------------------------------------------
  // rebuild friend rows
  // ---------------------------------------------------------------------------

  const rebuildRows = (friends: FriendEntry[], contentW: number) => {
    // destroy old children of listInner
    while (listInner.children.length > 0) {
      listInner.removeChildAt(0).destroy({ children: true });
    }

    // reset group header hit areas for drag-and-drop
    groupHeaderHitAreas = [];

    const maxNameChars = Math.max(
      6,
      Math.floor((contentW - ROW_AVATAR_SIZE - ROW_PADDING_X * 4) / (ROW_NAME_SIZE * 0.55))
    );

    // group friends by their group field, plus the reserved hub-nodes
    // section — see `buildFriendRowItems()`'s doc comment (helpers.ts) for
    // the full grouping/sort-order/hub-precedence rules.
    const items = buildFriendRowItems(friends, collapsedGroups);

    let friendRowIndex = 0;
    let currentY = 0;

    for (const item of items) {
      if (item.type === "header") {
        // render group header row
        const headerRow = new Container();
        headerRow.eventMode = "static";
        headerRow.cursor = "pointer";
        headerRow.hitArea = new Rectangle(0, 0, contentW, ROW_HEIGHT);
        headerRow.y = currentY;
        listInner.addChild(headerRow);

        const headerBg = new Graphics();
        headerBg.eventMode = "none";
        headerBg.rect(0, 0, contentW, ROW_HEIGHT);
        headerBg.fill({ color: 0x1c1c28 });
        headerRow.addChild(headerBg);

        // the reserved hub-nodes section (see HUB_GROUP_KEY's doc comment
        // above) uses the same header UI as a real group, but is never
        // renameable/deletable and never a drag-and-drop target.
        const isReservedHeader = item.group === HUB_GROUP_KEY;
        const headerDisplayName = isReservedHeader ? HUB_GROUP_LABEL : item.group;

        // colored group indicator line on header left edge
        const headerLineColor = colorForName(headerDisplayName, 0);
        const headerLine = new Graphics();
        headerLine.eventMode = "none";
        headerLine.rect(GROUP_LINE_INSET, 0, GROUP_LINE_WIDTH, ROW_HEIGHT);
        headerLine.fill({ color: headerLineColor });
        headerRow.addChild(headerLine);

        const isCollapsed = collapsedGroups.has(item.group);
        const chevronChar = isCollapsed ? "\u25b8" : "\u25be";
        const chevronText = new Text({
          text: chevronChar,
          style: { fontFamily: FONT, fontSize: 11, fill: LABEL_COLOR },
          resolution: RESOLUTION,
        });
        chevronText.eventMode = "none";
        chevronText.x = ROW_PADDING_X;
        chevronText.y = (ROW_HEIGHT - 11) / 2;
        headerRow.addChild(chevronText);

        if (editingGroupName === item.group) {
          // inline rename mode — show SkeinInput instead of static text
          const inputX = ROW_PADDING_X + chevronText.width + 6;
          const inputW = contentW - inputX - ROW_PADDING_X - 30;

          if (groupRenameHandle) {
            groupRenameHandle.destroy();
            groupRenameHandle = null;
          }

          const oldGroupName = item.group;
          groupRenameHandle = createSkeinInput({
            canvasElement: ctx.canvasElement,
            width: inputW,
            height: ROW_HEIGHT - 8,
            value: item.group,
            fontSize: 11,
            onEnter: (newName: string) => {
              const trimmed = newName.trim();
              if (trimmed && trimmed !== oldGroupName) {
                ctx.doc.change((draft) => {
                  const g = draft.groups.find((gr) => gr.name === oldGroupName);
                  if (g) g.name = trimmed;
                  for (const f of draft.friends) {
                    if (f.group === oldGroupName) f.group = trimmed;
                  }
                });
              }
              editingGroupName = null;
              if (groupRenameHandle) {
                groupRenameHandle.destroy();
                groupRenameHandle = null;
              }
              layout(currentWidth, currentHeight);
            },
          });
          groupRenameHandle.input.x = inputX;
          groupRenameHandle.input.y = (ROW_HEIGHT - (ROW_HEIGHT - 8)) / 2;
          headerRow.addChild(groupRenameHandle.input);

          // escape key handler to cancel rename
          const escHandler = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") {
              ev.stopPropagation();
              editingGroupName = null;
              if (groupRenameHandle) {
                groupRenameHandle.destroy();
                groupRenameHandle = null;
              }
              window.removeEventListener("keydown", escHandler, true);
              layout(currentWidth, currentHeight);
            }
          };
          window.addEventListener("keydown", escHandler, true);

          // auto-focus after a tick
          setTimeout(() => groupRenameHandle?.focus(), 50);
        } else {
          // normal group name text
          const groupNameText = new Text({
            text: headerDisplayName,
            style: { fontFamily: FONT, fontSize: 11, fontWeight: "bold", fill: LABEL_COLOR },
            resolution: RESOLUTION,
          });
          groupNameText.eventMode = "none";
          groupNameText.x = ROW_PADDING_X + chevronText.width + 6;
          groupNameText.y = (ROW_HEIGHT - 11) / 2;
          headerRow.addChild(groupNameText);
        }
        const countText = new Text({
          text: String(item.count),
          style: { fontFamily: FONT, fontSize: 10, fill: MUTED_TEXT },
          resolution: RESOLUTION,
        });
        countText.eventMode = "none";
        countText.x = contentW - ROW_PADDING_X - countText.width;
        countText.y = (ROW_HEIGHT - 10) / 2;
        headerRow.addChild(countText);

        const groupName = item.group;

        // double-click detection — tap times stored in module-level Map
        // so they survive rebuildRows cycles
        headerRow.on("pointertap", (e) => {
          e.stopPropagation();
          // ignore taps while a long-press rename is pending
          if (editingGroupName === groupName) return;

          // reserved section: collapse/expand only, never renameable
          if (isReservedHeader) {
            if (collapsedGroups.has(groupName)) {
              collapsedGroups.delete(groupName);
            } else {
              collapsedGroups.add(groupName);
            }
            layout(currentWidth, currentHeight);
            return;
          }

          const now = Date.now();
          const prev = headerLastTapTime.get(groupName) || 0;
          headerLastTapTime.set(groupName, now);

          if (now - prev < 400) {
            // double-click — enter inline rename mode
            headerLastTapTime.delete(groupName);
            editingGroupName = groupName;
            layout(currentWidth, currentHeight);
            return;
          }
          if (collapsedGroups.has(groupName)) {
            collapsedGroups.delete(groupName);
          } else {
            collapsedGroups.add(groupName);
          }
          layout(currentWidth, currentHeight);
        });

        // long-press detection — hold to rename
        let headerHoldTimer: ReturnType<typeof setTimeout> | null = null;
        headerRow.on("pointerdown", () => {
          if (isReservedHeader) return; // reserved section is never renameable
          headerHoldTimer = setTimeout(() => {
            headerHoldTimer = null;
            editingGroupName = groupName;
            layout(currentWidth, currentHeight);
          }, HEADER_HOLD_MS);
        });
        headerRow.on("pointerup", () => {
          if (headerHoldTimer) {
            clearTimeout(headerHoldTimer);
            headerHoldTimer = null;
          }
        });
        headerRow.on("pointerupoutside", () => {
          if (headerHoldTimer) {
            clearTimeout(headerHoldTimer);
            headerHoldTimer = null;
          }
        });
        headerRow.on("pointermove", () => {
          if (headerHoldTimer) {
            clearTimeout(headerHoldTimer);
            headerHoldTimer = null;
          }
        });
        currentY += ROW_HEIGHT;

        // register header for drop hit testing — the reserved hub-nodes
        // section is never a valid drag-and-drop target (hub membership is
        // never assigned by dragging a friend into a group).
        if (!isReservedHeader) {
          groupHeaderHitAreas.push({ group: item.group, y: headerRow.y, height: ROW_HEIGHT });
        }
      } else {
        // render friend row
        const friend = item.friend;
        const i = friendRowIndex;
        friendRowIndex++;

        // compute bio and effective row height
        const bio = bestBio(friend);
        const effectiveRowHeight = bio ? ROW_HEIGHT_BIO : ROW_HEIGHT;

        const rowContainer = new Container();
        rowContainer.eventMode = "static";
        rowContainer.cursor = "pointer";
        rowContainer.hitArea = new Rectangle(0, 0, contentW, effectiveRowHeight);
        rowContainer.y = currentY;
        listInner.addChild(rowContainer);

        // alternating row background
        const rowBg = new Graphics();
        rowBg.eventMode = "none";
        rowBg.rect(0, 0, contentW, effectiveRowHeight);
        if (i % 2 === 1) {
          rowBg.fill({ color: ROW_ALT_BG, alpha: 0.5 });
        } else {
          rowBg.fill({ color: BG, alpha: 0.01 });
        }
        rowContainer.addChild(rowBg);

        // dim the row if it's currently being dragged
        if (dragState?.isDragging && dragState.friendId === friend.id) {
          rowContainer.alpha = DRAG_ROW_DIMMED_ALPHA;
        }

        // colored group indicator line on the left edge
        if (friend.group) {
          const groupLineColor = colorForName(friend.group, 0);
          const groupLine = new Graphics();
          groupLine.eventMode = "none";
          groupLine.rect(GROUP_LINE_INSET, 0, GROUP_LINE_WIDTH, effectiveRowHeight);
          groupLine.fill({ color: groupLineColor });
          rowContainer.addChild(groupLine);
        }
        // avatar circle with initial letter, async image overlay, and
        // online/offline status dot (see avatar-renderer.ts)
        const displayName = friendDisplayName(friend);
        const avatarX = ROW_PADDING_X + ROW_AVATAR_SIZE / 2;
        const avatarY = effectiveRowHeight / 2;
        const avatarUrl = friend.nodeIds.find((n) => n.avatarDataUrl)?.avatarDataUrl;
        const isAnyNodeOnline = friend.nodeIds.some((n) => bridgeIsOnline(n.nodeId));
        const hasPendingOutbound = friendHasPendingOutboundRequest(
          friend,
          ctx.doc.current.outboundRequests ?? []
        );

        renderAvatar({
          parent: rowContainer,
          cacheKey: `friend-avatar-${friend.id}`,
          centerX: avatarX,
          centerY: avatarY,
          size: ROW_AVATAR_SIZE,
          displayName,
          colorSeed: i,
          avatarUrl,
          online: isAnyNodeOnline,
        });

        // name text — shifted up if bio present
        const textX = ROW_PADDING_X + ROW_AVATAR_SIZE + ROW_PADDING_X;
        const nameY = bio
          ? effectiveRowHeight / 2 - ROW_NAME_SIZE - 1
          : (effectiveRowHeight - ROW_NAME_SIZE) / 2;

        // primary name label (bold) — username or alias
        const nameText = new Text({
          text: truncate(displayName || "unnamed", maxNameChars),
          style: {
            fontFamily: FONT,
            fontSize: ROW_NAME_SIZE,
            fontWeight: "bold",
            fill: TEXT_COLOR,
          },
          resolution: RESOLUTION,
        });
        nameText.eventMode = "none";
        nameText.x = textX;
        nameText.y = nameY;
        rowContainer.addChild(nameText);
        let nameLineEndX = textX + nameText.width;

        // if the friend has both a username and an alias, show the alias
        // after the name in italic parentheses: "bob (bestie)"
        if (friend.username && friend.alias) {
          const aliasText = new Text({
            text: ` (${truncate(friend.alias, 12)})`,
            style: {
              fontFamily: FONT,
              fontSize: ROW_NAME_SIZE,
              fontStyle: "italic",
              fill: MUTED_TEXT,
            },
            resolution: RESOLUTION,
          });
          aliasText.eventMode = "none";
          aliasText.x = textX + nameText.width;
          aliasText.y = nameY;
          rowContainer.addChild(aliasText);
          nameLineEndX = aliasText.x + aliasText.width;
        }

        // small "pending" badge for a friend whose sent request hasn't been
        // accepted yet — the friend entry itself already shows up in this
        // list right away (see the "add friend" submit handler below, which
        // pushes a friend entry immediately rather than waiting for
        // acceptance), so surface the outstanding status here too rather
        // than only in the requests tab's separate "sent requests" section.
        // labeled "pending · hub" instead of plain "pending" when the
        // target is a known canvas-sharing hub (see
        // `recordKnownHubNodeIds()`, friendz-bridge.ts) — a friend request
        // to a hub is expected to resolve quickly via its vouched-based
        // auto-accept, so distinguishing it from an ordinary pending
        // request to a person is useful context here.
        if (hasPendingOutbound) {
          const isHubTarget = friend.nodeIds.some((n) => isKnownHubNodeId(n.nodeId));
          const badgeText = new Text({
            text: isHubTarget ? "pending \u00b7 hub" : "pending",
            style: { fontFamily: FONT, fontSize: ROW_SUB_SIZE - 1, fill: MUTED_TEXT },
            resolution: RESOLUTION,
          });
          badgeText.eventMode = "none";
          const badgePadX = 6;
          const badgeH = ROW_SUB_SIZE + 5;
          const badgeW = badgeText.width + badgePadX * 2;
          const badgeBg = new Graphics();
          badgeBg.eventMode = "none";
          badgeBg.roundRect(0, 0, badgeW, badgeH, badgeH / 2);
          badgeBg.stroke({ color: MUTED_TEXT, width: 1 });
          const badge = new Container();
          badge.eventMode = "none";
          badge.addChild(badgeBg);
          badgeText.x = badgePadX;
          badgeText.y = (badgeH - badgeText.height) / 2;
          badge.addChild(badgeText);
          badge.x = nameLineEndX + 8;
          badge.y = nameY + (ROW_NAME_SIZE - badgeH) / 2;
          rowContainer.addChild(badge);
        }

        // bio line — second row of text under the name
        if (bio) {
          const maxBioChars = Math.max(
            10,
            Math.floor((contentW - textX - ROW_PADDING_X - 20) / (ROW_SUB_SIZE * 0.55))
          );
          const bioText = new Text({
            text: truncate(bio, maxBioChars),
            style: { fontFamily: FONT, fontSize: ROW_SUB_SIZE, fill: MUTED_TEXT },
            resolution: RESOLUTION,
          });
          bioText.eventMode = "none";
          bioText.x = textX;
          bioText.y = effectiveRowHeight / 2 + 2;
          rowContainer.addChild(bioText);
        }

        // chevron hint on the right
        const chevron = new Text({
          text: "\u203a",
          style: { fontFamily: FONT, fontSize: 16, fill: MUTED_TEXT },
          resolution: RESOLUTION,
        });
        chevron.eventMode = "none";
        chevron.x = contentW - ROW_PADDING_X - chevron.width;
        chevron.y = (effectiveRowHeight - 16) / 2;
        rowContainer.addChild(chevron);

        // drag-aware pointer events (replaces simple pointertap)
        const friendId = friend.id;
        const friendGroup = friend.group;

        rowContainer.on("pointerdown", (e) => {
          if (dragState) return;
          const gx = e.globalX ?? (e as any).global?.x ?? 0;
          const gy = e.globalY ?? (e as any).global?.y ?? 0;
          dragState = {
            friendId,
            friendGroup,
            startX: gx,
            startY: gy,
            holdTimer: setTimeout(() => {
              if (dragState && dragState.friendId === friendId && !dragState.isDragging) {
                startDrag(friendId, friendGroup, gx, gy);
              }
            }, DRAG_HOLD_MS),
            isDragging: false,
            ghost: null,
          };
        });

        rowContainer.on("globalpointermove", (e) => {
          if (!dragState || dragState.friendId !== friendId) return;
          const gx = e.globalX ?? (e as any).global?.x ?? 0;
          const gy = e.globalY ?? (e as any).global?.y ?? 0;
          const dx = gx - dragState.startX;
          const dy = gy - dragState.startY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (!dragState.isDragging && dist > DRAG_THRESHOLD) {
            if (dragState.holdTimer) clearTimeout(dragState.holdTimer);
            startDrag(friendId, friendGroup, gx, gy);
          } else if (dragState.isDragging) {
            updateDrag(gx, gy);
          }
        });

        rowContainer.on("pointerup", (e) => {
          if (!dragState || dragState.friendId !== friendId) return;
          const gx = e.globalX ?? (e as any).global?.x ?? 0;
          const gy = e.globalY ?? (e as any).global?.y ?? 0;
          const result = endDrag(gx, gy);

          if (result === "tap") {
            // normal tap — open detail view. if this friend hasn't accepted
            // our request yet, ask any currently-online mutual friends to
            // relay it and hand back profile info sooner, rather than
            // waiting for the recipient's next online-transition.
            if (hasPendingOutbound) {
              gossipFriendRequestsNow();
            }
            selectedFriendId = friendId;
            viewMode = "detail";
            scrollY = 0;
            layout(currentWidth, currentHeight);
          }
        });

        rowContainer.on("pointerupoutside", (e) => {
          if (!dragState || dragState.friendId !== friendId) return;
          const gx = e.globalX ?? (e as any).global?.x ?? 0;
          const gy = e.globalY ?? (e as any).global?.y ?? 0;
          endDrag(gx, gy);
        });

        // hover effect
        rowContainer.on("pointerover", () => {
          if (dragState?.isDragging) return;
          rowBg.clear();
          rowBg.rect(0, 0, contentW, effectiveRowHeight);
          rowBg.fill({ color: 0x252536, alpha: 0.8 });
        });
        rowContainer.on("pointerout", () => {
          if (dragState?.isDragging) return;
          rowBg.clear();
          rowBg.rect(0, 0, contentW, effectiveRowHeight);
          if (i % 2 === 1) {
            rowBg.fill({ color: ROW_ALT_BG, alpha: 0.5 });
          } else {
            rowBg.fill({ color: BG, alpha: 0.01 });
          }
        });

        currentY += effectiveRowHeight;
      }
    }

    totalListHeight = currentY;
  };

  // ---------------------------------------------------------------------------
  // detail view (scrollable, masked — same pattern the friend-list area
  // above uses. added when the friend-canvas-bin section could push total
  // detail content past the social panel's own fixed bounds with nothing
  // to clip/scroll it — a real user-reported bug, 2026-07-02).
  // ---------------------------------------------------------------------------

  const detailContainer = new Container();
  detailContainer.eventMode = "static";
  detailContainer.visible = false;
  container.addChild(detailContainer);

  const detailMask = new Graphics();
  detailContainer.addChild(detailMask);

  let detailScrollY = 0;
  let detailAreaHeight = 0;
  // total content height, tracked manually as `rebuildDetailView()` lays
  // things out (never read via `detailInner.height`) — `detailInner` has a
  // `.mask` set, and asking Pixi v8 for `.height`/`.getBounds()` on a
  // masked object makes it walk the mask's matrix relative to a shared
  // root to compute "effective" bounds, which both logs a "Mask bounds,
  // renderable is not inside the root container" warning AND can break
  // rendering outright (a real, serious user-reported bug, 2026-07-02: the
  // whole profile view went blank from the identical mistake in
  // profile-tab.ts). `bin/bin-renderer.ts`'s `totalContentHeight` field is
  // the established safe alternative this mirrors.
  let detailContentHeight = 0;

  const clampDetailScroll = () => {
    const maxScroll = Math.max(0, detailContentHeight - detailAreaHeight);
    detailScrollY = Math.max(0, Math.min(detailScrollY, maxScroll));
    detailInner.y = -detailScrollY;
  };

  detailContainer.on("wheel", (e: WheelEvent) => {
    const canScroll = detailContentHeight > detailAreaHeight;
    if (!canScroll) return; // let the event pass through to the canvas viewport

    e.stopPropagation();
    // claim the native event so the viewport doesn't also pan
    if ((e as any).nativeEvent) (e as any).nativeEvent._skeinWidgetScroll = true;
    detailScrollY += e.deltaY > 0 ? SCROLL_SPEED : -SCROLL_SPEED;
    clampDetailScroll();
  });

  const detailInner = new Container();
  detailInner.eventMode = "static";
  detailContainer.addChild(detailInner);
  detailInner.mask = detailMask;

  // ---------------------------------------------------------------------------
  // hub-profile-panel view — mounted for a friend-detail view where
  // `friend.isHub === true`, docs/hub-and-profile-plan.md section 5/8 step 7.
  // follows the exact same "inline replacement" navigation pattern the
  // list/detail/add sub-views already use (a dedicated viewMode + container,
  // not a floating overlay), for consistency.
  // ---------------------------------------------------------------------------

  const hubProfileContainer = new Container();
  hubProfileContainer.eventMode = "static";
  hubProfileContainer.visible = false;
  container.addChild(hubProfileContainer);

  const hubProfileBackBtn = new Container();
  hubProfileBackBtn.eventMode = "static";
  hubProfileBackBtn.cursor = "pointer";
  const hubProfileBackText = new Text({
    text: "\u2039 back",
    style: { fontFamily: FONT, fontSize: TAB_FONT_SIZE, fill: ACCENT },
    resolution: RESOLUTION,
  });
  hubProfileBackText.eventMode = "none";
  hubProfileBackBtn.addChild(hubProfileBackText);
  hubProfileBackBtn.hitArea = new Rectangle(0, 0, hubProfileBackText.width + 8, hubProfileBackText.height + 4);
  hubProfileBackBtn.on("pointertap", (e) => {
    e.stopPropagation();
    closeHubProfilePanelView();
  });
  hubProfileContainer.addChild(hubProfileBackBtn);

  const hubProfilePanelHost = new Container();
  hubProfileContainer.addChild(hubProfilePanelHost);

  const HUB_PROFILE_BACK_AREA_HEIGHT = hubProfileBackText.height + 12;

  /** tear down the mounted hub-profile-panel (if any) and return to the friend-detail view. */
  function closeHubProfilePanelView(): void {
    if (hubProfileHandle) {
      hubProfileHandle.destroy();
      hubProfileHandle = null;
    }
    hubProfileHubNodeId = null;
    viewMode = "detail";
    layout(currentWidth, currentHeight);
  }

  /** mount (or, if already mounted for the same hub node id, just re-layout) the hub-profile-panel. */
  function mountOrLayoutHubProfilePanel(hubNodeId: string, w: number, h: number): void {
    hubProfileBackBtn.x = 0;
    hubProfileBackBtn.y = 0;
    hubProfilePanelHost.x = 0;
    hubProfilePanelHost.y = HUB_PROFILE_BACK_AREA_HEIGHT;

    const panelHeight = Math.max(0, h - HUB_PROFILE_BACK_AREA_HEIGHT);

    if (hubProfileHandle && hubProfileHubNodeId === hubNodeId) {
      hubProfileHandle.layout(w, panelHeight);
      return;
    }

    if (hubProfileHandle) {
      hubProfileHandle.destroy();
      hubProfileHandle = null;
    }

    hubProfileHubNodeId = hubNodeId;
    const client = createHubAdminClient(getHubAdminTransport());
    hubProfileHandle = mountHubProfilePanel(hubProfilePanelHost, {
      hubNodeId,
      client,
      canvasElement: ctx.canvasElement,
    });
    hubProfileHandle.layout(w, panelHeight);
  }

  const rebuildDetailView = (friend: FriendEntry, contentW: number) => {
    // clean up any existing input handles before destroying children — but
    // preserve any alias text the user is actively mid-typing. this view
    // rebuilds on every doc "change" event (see the doc.on("change", ...)
    // subscription below), which fires for ANY change anywhere in the
    // shared social doc — presence probes, gossip digest merges, profile
    // refreshes, other friends' status — not just this friend, so without
    // this the alias input was getting silently destroyed/recreated with
    // the old saved value mid-edit (a real user-reported bug: typing a new
    // alias appeared to do nothing).
    const preservedAliasValue = editingAlias && aliasInputHandle ? aliasInputHandle.value : null;
    if (aliasInputHandle) {
      aliasInputHandle.destroy();
      aliasInputHandle = null;
    }
    if (groupInputHandle) {
      groupInputHandle.destroy();
      groupInputHandle = null;
    }
    if (friendCanvasBinController) {
      const c = friendCanvasBinController.container;
      friendCanvasBinController.destroy();
      if (c.parent) c.parent.removeChild(c);
      friendCanvasBinController = null;
      registerSocialBridge({ friendCanvasBin: undefined });
    }
    detailNodeIdTextRef = null;
    // fresh friend/detail view — don't carry over a scroll offset from
    // whichever friend (or lack thereof) was viewed previously.
    detailScrollY = 0;

    while (detailInner.children.length > 0) {
      detailInner.removeChildAt(0).destroy({ children: true });
    }

    let dy = 0;

    // back button
    const backBtn = new Container();
    backBtn.eventMode = "static";
    backBtn.cursor = "pointer";
    const backText = new Text({
      text: "\u2039 friends",
      style: { fontFamily: FONT, fontSize: TAB_FONT_SIZE, fill: ACCENT },
      resolution: RESOLUTION,
    });
    backText.eventMode = "none";
    backBtn.addChild(backText);
    backBtn.hitArea = new Rectangle(0, 0, backText.width + 8, backText.height + 4);
    backBtn.on("pointertap", (e) => {
      e.stopPropagation();
      selectedFriendId = null;
      viewMode = "list";
      editingAlias = false;
      editingNewGroup = false;
      scrollY = 0;
      layout(currentWidth, currentHeight);
    });
    detailInner.addChild(backBtn);
    dy += backText.height + 12;

    // avatar — large centered circle
    const displayName = friendDisplayName(friend);
    const avatarColor = colorForName(displayName, 0);
    const avatarCx = contentW / 2;
    const avatarCy = dy + DETAIL_AVATAR_SIZE / 2;

    const avatarUrl = friend.nodeIds.find((n) => n.avatarDataUrl)?.avatarDataUrl;

    const avatarCircle = new Graphics();
    avatarCircle.eventMode = "none";
    avatarCircle.circle(avatarCx, avatarCy, DETAIL_AVATAR_SIZE / 2);
    avatarCircle.fill({ color: avatarColor });
    detailInner.addChild(avatarCircle);

    const initial = displayName.charAt(0).toUpperCase() || "?";
    const avatarInitial = new Text({
      text: initial,
      style: {
        fontFamily: FONT,
        fontSize: 20,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
      },
      resolution: RESOLUTION,
    });
    avatarInitial.eventMode = "none";
    avatarInitial.anchor.set(0.5);
    avatarInitial.x = avatarCx;
    avatarInitial.y = avatarCy;
    detailInner.addChild(avatarInitial);

    if (avatarUrl) {
      // helper to overlay the avatar sprite once we have a texture
      const applyAvatarTexture = (texture: Texture) => {
        if (avatarCircle.destroyed) return;
        const avatarSprite = new Sprite(texture);
        avatarSprite.eventMode = "none";
        avatarSprite.width = DETAIL_AVATAR_SIZE;
        avatarSprite.height = DETAIL_AVATAR_SIZE;
        avatarSprite.x = avatarCx - DETAIL_AVATAR_SIZE / 2;
        avatarSprite.y = avatarCy - DETAIL_AVATAR_SIZE / 2;

        const spriteMask = new Graphics();
        spriteMask.circle(avatarCx, avatarCy, DETAIL_AVATAR_SIZE / 2);
        spriteMask.fill({ color: 0xffffff });
        detailInner.addChild(spriteMask);
        avatarSprite.mask = spriteMask;
        detailInner.addChild(avatarSprite);

        avatarCircle.visible = false;
        avatarInitial.visible = false;
      };

      // try synchronous cache first (list view may have loaded it already)
      const cacheKey = `friend-avatar-${friend.id}`;
      const cached = Assets.get<Texture>(cacheKey);
      if (cached) {
        applyAvatarTexture(cached);
      } else {
        // fallback: load via an Image element (data URLs resolve ~instantly)
        const img = new Image();
        img.onload = () => {
          try {
            const texture = Texture.from({ resource: img, label: cacheKey });
            applyAvatarTexture(texture);
          } catch {
            // texture creation failed — keep the fallback circle
          }
        };
        img.src = avatarUrl;
      }
    }

    // online/offline presence dot — overlaid on the avatar's bottom-right
    // corner (a small ring-bordered dot, standard presence-indicator
    // convention) rather than its own separate label+dot line, to free up
    // vertical space for the friend-canvas-bin section below (a real
    // user-reported request, 2026-07-02).
    const isOnline = friend.nodeIds.some((n) => bridgeIsOnline(n.nodeId));
    const statusDot = new Graphics();
    statusDot.eventMode = "none";
    const statusColor = isOnline ? ONLINE_COLOR : OFFLINE_COLOR;
    const dotR = ONLINE_DOT_SIZE / 2 + 1;
    const dotCx = avatarCx + DETAIL_AVATAR_SIZE / 2 - dotR;
    const dotCy = avatarCy + DETAIL_AVATAR_SIZE / 2 - dotR;
    // ring border matches the panel background, so the dot reads as
    // "cut into" the avatar rather than floating on top of it.
    statusDot.circle(dotCx, dotCy, dotR + 1.5).fill({ color: BG });
    statusDot.circle(dotCx, dotCy, dotR).fill({ color: statusColor });
    detailInner.addChild(statusDot);

    dy += DETAIL_AVATAR_SIZE + 10;

    // display name — centered
    const nameText = new Text({
      text: friendDisplayNameFull(friend),
      style: {
        fontFamily: FONT,
        fontSize: DETAIL_NAME_SIZE,
        fontWeight: "bold",
        fill: TEXT_COLOR,
      },
      resolution: RESOLUTION,
    });
    nameText.eventMode = "none";
    nameText.x = Math.max(0, (contentW - nameText.width) / 2);
    nameText.y = dy;
    detailInner.addChild(nameText);
    dy += DETAIL_NAME_SIZE + 6;

    // -----------------------------------------------------------------------
    // presence probe — actively re-check this friend's online status by
    // sending a one-shot heartbeat to each of their known node ids, rather
    // than relying solely on the last passively-received heartbeat (which
    // can be stale by up to HEARTBEAT_TIMEOUT_MS). any reply updates
    // presence state and fires onOnlineChange, which re-renders this view
    // through the existing online-status subscription. fires at most once
    // per detail-view visit (presenceProbeRequestedFor, cleared by layout()
    // on leaving "detail"). best-effort — no error UI if a probe fails.
    // -----------------------------------------------------------------------

    if (presenceProbeRequestedFor !== friend.id) {
      presenceProbeRequestedFor = friend.id;
      for (const { nodeId } of friend.nodeIds) {
        probePeer(nodeId).catch((err) => {
          log.warn("social.friends", "presence probe failed for", nodeId.slice(0, 16) + "...", err);
        });
      }
    }

    // -----------------------------------------------------------------------
    // live profile refresh — the friend-detail view otherwise only shows
    // whatever username/bio/avatar/isHub snapshot was last learned via
    // gossip relay or a prior profile-response (see friendz-wiring.ts's
    // onProfileResponse), which can go stale. if the friend is currently
    // online, ask them directly for their latest profile; the response
    // writes back into ctx.doc (onProfileResponse) and this view re-renders
    // through the existing doc-change subscription — never blocks this
    // (already-rendered) frame. fires at most once per detail-view visit
    // (profileRefreshRequestedFor, cleared by layout() on leaving "detail"),
    // and shows a small, minimal "refreshing..." indicator for the
    // duration — best-effort, no error UI if the request fails or times
    // out (matches this file's existing conventions elsewhere).
    // -----------------------------------------------------------------------

    if (profileRefreshRequestedFor !== friend.id) {
      profileRefreshRequestedFor = friend.id;
      const onlineNodeId = friend.nodeIds.find((n) => bridgeIsOnline(n.nodeId))?.nodeId;
      if (onlineNodeId) {
        profileRefreshPendingFor = friend.id;
        requestProfile(onlineNodeId)
          .catch((err) => {
            log.warn("social.friends", "live profile refresh failed for", onlineNodeId.slice(0, 16) + "...", err);
          })
          .finally(() => {
            if (profileRefreshPendingFor !== friend.id) return;
            profileRefreshPendingFor = null;
            if (detailInner.destroyed) return;
            if (selectedFriendId === friend.id && viewMode === "detail") {
              layout(currentWidth, currentHeight);
            }
          });
      }
    }

    if (profileRefreshPendingFor === friend.id) {
      const refreshingText = new Text({
        text: "refreshing profile\u2026",
        style: { fontFamily: FONT, fontSize: 10, fill: MUTED_TEXT },
        resolution: RESOLUTION,
      });
      refreshingText.eventMode = "none";
      refreshingText.x = Math.max(0, (contentW - refreshingText.width) / 2);
      refreshingText.y = dy;
      detailInner.addChild(refreshingText);
      dy += refreshingText.height + 6;
    }

    // -----------------------------------------------------------------------
    // manage hub — only shown for friends flagged as a reliquary hub
    // (docs/hub-and-profile-plan.md section 5/8 step 7).
    // -----------------------------------------------------------------------

    if (friend.isHub) {
      const hubNodeIdForFriend = friend.nodeIds[0]?.nodeId;
      const hubOnline = !!hubNodeIdForFriend && bridgeIsOnline(hubNodeIdForFriend);

      const manageHubBtn = new Container();
      manageHubBtn.eventMode = hubOnline ? "static" : "none";
      manageHubBtn.cursor = hubOnline ? "pointer" : "default";
      manageHubBtn.hitArea = new Rectangle(0, 0, contentW, DETAIL_BTN_HEIGHT);
      manageHubBtn.y = dy;
      detailInner.addChild(manageHubBtn);

      const manageHubBg = new Graphics();
      manageHubBg.eventMode = "none";
      manageHubBg.roundRect(0, 0, contentW, DETAIL_BTN_HEIGHT, DETAIL_BTN_RADIUS);
      manageHubBg.fill({ color: hubOnline ? ACCENT : OFFLINE_COLOR });
      manageHubBtn.addChild(manageHubBg);

      const manageHubText = new Text({
        text: hubOnline ? "manage hub" : "manage hub (offline)",
        style: { fontFamily: FONT, fontSize: TEXT_SIZE, fill: hubOnline ? 0xffffff : MUTED_TEXT },
        resolution: RESOLUTION,
      });
      manageHubText.eventMode = "none";
      manageHubText.x = (contentW - manageHubText.width) / 2;
      manageHubText.y = (DETAIL_BTN_HEIGHT - TEXT_SIZE) / 2;
      manageHubBtn.addChild(manageHubText);

      if (hubOnline) {
        manageHubBtn.on("pointertap", (e) => {
          e.stopPropagation();
          if (!hubNodeIdForFriend) {
            log.warn("social.friends", "hub friend has no node id to administer:", friend.id);
            return;
          }
          viewMode = "hubProfile";
          layout(currentWidth, currentHeight);
        });
      }

      dy += DETAIL_BTN_HEIGHT + 10;

    }

    // -----------------------------------------------------------------------
    // bio (if any nodeId has one) — shown before alias (a real user-
    // reported layout preference, 2026-07-02: alias editing used to come
    // first).
    // -----------------------------------------------------------------------

    const bio = friend.nodeIds.find((n) => n.bio)?.bio;
    if (bio) {
      const bioText = new Text({
        text: truncate(bio, 80),
        style: {
          fontFamily: FONT,
          fontSize: DETAIL_BIO_SIZE,
          fill: MUTED_TEXT,
          wordWrap: true,
          wordWrapWidth: contentW,
        },
        resolution: RESOLUTION,
      });
      bioText.eventMode = "none";
      bioText.x = Math.max(0, (contentW - bioText.width) / 2);
      bioText.y = dy;
      detailInner.addChild(bioText);
      dy += bioText.height + 10;
    } else {
      dy += 4;
    }

    // -----------------------------------------------------------------------
    // alias section — inline editing
    // -----------------------------------------------------------------------

    const aliasLabel = new Text({
      text: "alias",
      style: { fontFamily: FONT, fontSize: LABEL_SIZE, fill: LABEL_COLOR },
      resolution: RESOLUTION,
    });
    aliasLabel.eventMode = "none";
    aliasLabel.x = 0;
    aliasLabel.y = dy;
    detailInner.addChild(aliasLabel);
    dy += LABEL_SIZE + 4;

    if (editingAlias) {
      // editing mode — show input field with save/cancel
      aliasInputHandle = createSkeinInput({
        canvasElement: ctx.canvasElement,
        width: contentW,
        height: FIELD_HEIGHT,
        value: preservedAliasValue ?? friend.alias,
        onChange: () => {},
      });
      aliasInputHandle.input.x = 0;
      aliasInputHandle.input.y = dy;
      detailInner.addChild(aliasInputHandle.input);
      dy += FIELD_HEIGHT + 6;

      // save button
      const aliasSaveBtn = new Container();
      aliasSaveBtn.eventMode = "static";
      aliasSaveBtn.cursor = "pointer";
      const aliasSaveBg = new Graphics();
      aliasSaveBg.eventMode = "none";
      const aliasSaveText = new Text({
        text: "save",
        style: { fontFamily: FONT, fontSize: 9, fill: 0xffffff },
        resolution: RESOLUTION,
      });
      aliasSaveText.eventMode = "none";
      const savePadX = 10;
      const savePadY = 4;
      const saveW = aliasSaveText.width + savePadX * 2;
      const saveH = aliasSaveText.height + savePadY * 2;
      aliasSaveBg.roundRect(0, 0, saveW, saveH, 3);
      aliasSaveBg.fill({ color: ACCENT });
      aliasSaveBtn.addChild(aliasSaveBg);
      aliasSaveText.x = savePadX;
      aliasSaveText.y = savePadY;
      aliasSaveBtn.addChild(aliasSaveText);
      aliasSaveBtn.hitArea = new Rectangle(0, 0, saveW, saveH);
      aliasSaveBtn.x = 0;
      aliasSaveBtn.y = dy;
      const savedFriendId = friend.id;
      aliasSaveBtn.on("pointertap", (e) => {
        e.stopPropagation();
        const newValue = aliasInputHandle ? aliasInputHandle.value.trim() : "";
        editingAlias = false;
        ctx.doc.change((draft) => {
          const idx = draft.friends.findIndex((f: FriendEntry) => f.id === savedFriendId);
          if (idx !== -1) draft.friends[idx].alias = newValue;
        });
      });
      detailInner.addChild(aliasSaveBtn);

      // cancel button
      const aliasCancelBtn = new Container();
      aliasCancelBtn.eventMode = "static";
      aliasCancelBtn.cursor = "pointer";
      const aliasCancelBg = new Graphics();
      aliasCancelBg.eventMode = "none";
      const aliasCancelText = new Text({
        text: "cancel",
        style: { fontFamily: FONT, fontSize: 9, fill: MUTED_TEXT },
        resolution: RESOLUTION,
      });
      aliasCancelText.eventMode = "none";
      const cancelPadX = 10;
      const cancelPadY = 4;
      const cancelW = aliasCancelText.width + cancelPadX * 2;
      const cancelH = aliasCancelText.height + cancelPadY * 2;
      aliasCancelBg.roundRect(0, 0, cancelW, cancelH, 3);
      aliasCancelBg.fill({ color: FIELD_BG });
      aliasCancelBg.stroke({ color: FIELD_BORDER, width: 1 });
      aliasCancelBtn.addChild(aliasCancelBg);
      aliasCancelText.x = cancelPadX;
      aliasCancelText.y = cancelPadY;
      aliasCancelBtn.addChild(aliasCancelText);
      aliasCancelBtn.hitArea = new Rectangle(0, 0, cancelW, cancelH);
      aliasCancelBtn.x = saveW + 6;
      aliasCancelBtn.y = dy;
      aliasCancelBtn.on("pointertap", (e) => {
        e.stopPropagation();
        editingAlias = false;
        layout(currentWidth, currentHeight);
      });
      detailInner.addChild(aliasCancelBtn);

      dy += saveH + 8;
    } else {
      // display mode — show alias value with edit button
      const aliasRow = new Container();
      aliasRow.eventMode = "static";
      aliasRow.y = dy;
      detailInner.addChild(aliasRow);

      const aliasValue = friend.alias || "none";
      const aliasValueText = new Text({
        text: aliasValue,
        style: {
          fontFamily: FONT,
          fontSize: DETAIL_NODEID_SIZE,
          fill: friend.alias ? TEXT_COLOR : MUTED_TEXT,
        },
        resolution: RESOLUTION,
      });
      aliasValueText.eventMode = "none";
      aliasValueText.y = 2;
      aliasRow.addChild(aliasValueText);

      // edit button — pill-shaped
      const aliasEditBtn = new Container();
      aliasEditBtn.eventMode = "static";
      aliasEditBtn.cursor = "pointer";
      aliasEditBtn.x = aliasValueText.width + 8;
      const aliasEditBg = new Graphics();
      aliasEditBg.eventMode = "none";
      const aliasEditLabel = new Text({
        text: "edit",
        style: { fontFamily: FONT, fontSize: 9, fill: ACCENT },
        resolution: RESOLUTION,
      });
      aliasEditLabel.eventMode = "none";
      const editPadX = 8;
      const editPadY = 3;
      const editW = aliasEditLabel.width + editPadX * 2;
      const editH = aliasEditLabel.height + editPadY * 2;
      aliasEditBg.roundRect(0, 0, editW, editH, 3);
      aliasEditBg.fill({ color: FIELD_BG });
      aliasEditBg.stroke({ color: FIELD_BORDER, width: 1 });
      aliasEditBtn.addChild(aliasEditBg);
      aliasEditLabel.x = editPadX;
      aliasEditLabel.y = editPadY;
      aliasEditBtn.addChild(aliasEditLabel);
      aliasEditBtn.hitArea = new Rectangle(0, 0, editW, editH);
      aliasEditBtn.on("pointertap", (e) => {
        e.stopPropagation();
        editingAlias = true;
        layout(currentWidth, currentHeight);
      });
      aliasRow.addChild(aliasEditBtn);

      dy += editH + 8;
    }

    // separator
    const sep = new Graphics();
    sep.moveTo(0, dy);
    sep.lineTo(contentW, dy);
    sep.stroke({ color: BORDER, width: 1, alpha: 0.5 });
    detailInner.addChild(sep);
    dy += 10;

    // -----------------------------------------------------------------------
    // node IDs section
    // -----------------------------------------------------------------------

    for (const nodeEntry of friend.nodeIds) {
      if (!nodeEntry.nodeId) continue;

      // "node id" label + abbreviated value + copy button all on one row
      // (previously the label had its own line above — merged to free up
      // vertical space for the friend-canvas-bin section below, a real
      // user-reported request, 2026-07-02).
      const nodeIdRow = new Container();
      nodeIdRow.eventMode = "static";
      nodeIdRow.y = dy;
      detailInner.addChild(nodeIdRow);

      const nodeIdLabel = new Text({
        text: "node id",
        style: { fontFamily: FONT, fontSize: LABEL_SIZE, fill: LABEL_COLOR },
        resolution: RESOLUTION,
      });
      nodeIdLabel.eventMode = "none";
      nodeIdLabel.y = 2;
      nodeIdRow.addChild(nodeIdLabel);

      const abbreviated = nodeEntry.nodeId.slice(0, 8) + "\u2026" + nodeEntry.nodeId.slice(-8);
      const nodeIdText = new Text({
        text: abbreviated,
        style: {
          fontFamily: FONT,
          fontSize: DETAIL_NODEID_SIZE,
          fill: TEXT_COLOR,
        },
        resolution: RESOLUTION,
      });
      nodeIdText.eventMode = "none";
      nodeIdText.x = nodeIdLabel.width + 8;
      nodeIdText.y = 2;
      nodeIdRow.addChild(nodeIdText);

      // copy button
      const copyBtn = new Container();
      copyBtn.eventMode = "static";
      copyBtn.cursor = "pointer";
      copyBtn.x = nodeIdText.x + nodeIdText.width + 8;

      const copyBtnBg = new Graphics();
      copyBtnBg.eventMode = "none";
      const copyLabelStyle = { fontFamily: FONT, fontSize: 9, fill: ACCENT };
      const copyLabel = new Text({
        text: "copy",
        style: copyLabelStyle,
        resolution: RESOLUTION,
      });
      copyLabel.eventMode = "none";
      const copyPadX = 8;
      const copyPadY = 3;
      // size the button for whichever label ("copy" / "copied!") is wider, so it
      // never needs to resize (and never overflows) when the label swaps in.
      const copiedLabelWidth = new Text({ text: "copied!", style: copyLabelStyle, resolution: RESOLUTION }).width;
      const copyW = Math.max(copyLabel.width, copiedLabelWidth) + copyPadX * 2;
      const copyH = copyLabel.height + copyPadY * 2;
      copyBtnBg.roundRect(0, 0, copyW, copyH, 3);
      copyBtnBg.fill({ color: FIELD_BG });
      copyBtnBg.stroke({ color: FIELD_BORDER, width: 1 });
      copyBtn.addChild(copyBtnBg);
      copyLabel.x = copyPadX;
      copyLabel.y = copyPadY;
      copyBtn.addChild(copyLabel);
      copyBtn.hitArea = new Rectangle(0, 0, copyW, copyH);

      const fullNodeId = nodeEntry.nodeId;
      copyBtn.on("pointertap", (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(fullNodeId).then(
          () => {
            copyLabel.text = "copied!";
            setTimeout(() => {
              if (detailInner.destroyed) return;
              copyLabel.text = "copy";
            }, COPY_FEEDBACK_MS);
          },
          () => {}
        );
      });

      nodeIdRow.addChild(copyBtn);
      dy += copyH + 8;
    }

    // -----------------------------------------------------------------------
    // group — editable picker
    // -----------------------------------------------------------------------
    {
      const groupLabel = new Text({
        text: "group",
        style: { fontFamily: FONT, fontSize: LABEL_SIZE, fill: LABEL_COLOR },
        resolution: RESOLUTION,
      });
      groupLabel.eventMode = "none";
      groupLabel.y = dy;
      detailInner.addChild(groupLabel);
      dy += LABEL_SIZE + 6;

      // collect all known groups
      const knownGroups: string[] = (ctx.doc.current.groups ?? []).map((g: FriendGroup) => g.name);
      if (friend.group && !knownGroups.includes(friend.group)) {
        knownGroups.push(friend.group);
      }
      knownGroups.sort((a, b) => a.localeCompare(b));

      // build pill options: "none" + known groups + "+ new"
      const pillOptions: string[] = ["none", ...knownGroups, "+ new"];
      let px = 0;
      let pillRowY = dy;
      const friendIdForGroup = friend.id;

      for (const opt of pillOptions) {
        const isActive =
          (opt === "none" && !friend.group) ||
          (opt !== "none" && opt !== "+ new" && friend.group === opt);
        const pillW = Math.max(40, opt.length * (OPTION_FONT_SIZE * 0.65) + 20);

        // wrap to next line if exceeds contentW
        if (px + pillW > contentW && px > 0) {
          px = 0;
          pillRowY += OPTION_PILL_HEIGHT + OPTION_PILL_GAP;
        }

        const pill = new Container();
        pill.eventMode = "static";
        pill.cursor = "pointer";
        pill.hitArea = new Rectangle(0, 0, pillW, OPTION_PILL_HEIGHT);
        pill.x = px;
        pill.y = pillRowY;

        const pillBg = new Graphics();
        pillBg.roundRect(0, 0, pillW, OPTION_PILL_HEIGHT, OPTION_PILL_RADIUS);
        if (isActive) {
          pillBg.fill({ color: ACCENT });
        } else {
          pillBg.fill({ color: FIELD_BG });
          pillBg.stroke({ color: FIELD_BORDER, width: 1 });
        }
        pill.addChild(pillBg);

        const pillText = new Text({
          text: opt,
          style: {
            fontFamily: FONT,
            fontSize: OPTION_FONT_SIZE,
            fill: isActive ? 0xffffff : MUTED_TEXT,
          },
          resolution: RESOLUTION,
        });
        pillText.eventMode = "none";
        pillText.x = (pillW - pillText.width) / 2;
        pillText.y = (OPTION_PILL_HEIGHT - OPTION_FONT_SIZE) / 2;
        pill.addChild(pillText);

        const optValue = opt;
        pill.on("pointertap", (e) => {
          e.stopPropagation();
          if (optValue === "+ new") {
            editingNewGroup = true;
            layout(currentWidth, currentHeight);
          } else if (optValue === "none") {
            ctx.doc.change((draft) => {
              const idx = draft.friends.findIndex((f: FriendEntry) => f.id === friendIdForGroup);
              if (idx !== -1) draft.friends[idx].group = "";
            });
          } else {
            ctx.doc.change((draft) => {
              const idx = draft.friends.findIndex((f: FriendEntry) => f.id === friendIdForGroup);
              if (idx !== -1) draft.friends[idx].group = optValue;
            });
          }
        });

        detailInner.addChild(pill);
        px += pillW + OPTION_PILL_GAP;
      }

      dy = pillRowY + OPTION_PILL_HEIGHT + 8;

      // new group input (shown when "+ new" is clicked)
      if (editingNewGroup) {
        groupInputHandle = createSkeinInput({
          canvasElement: ctx.canvasElement,
          width: contentW,
          height: FIELD_HEIGHT,
          value: "",
          onChange: () => {},
        });
        groupInputHandle.input.x = 0;
        groupInputHandle.input.y = dy;
        detailInner.addChild(groupInputHandle.input);
        dy += FIELD_HEIGHT + 6;

        // confirm button
        const newGroupConfirmBtn = new Container();
        newGroupConfirmBtn.eventMode = "static";
        newGroupConfirmBtn.cursor = "pointer";
        const newGroupConfirmBg = new Graphics();
        newGroupConfirmBg.eventMode = "none";
        const newGroupConfirmText = new Text({
          text: "add group",
          style: { fontFamily: FONT, fontSize: 9, fill: 0xffffff },
          resolution: RESOLUTION,
        });
        newGroupConfirmText.eventMode = "none";
        const ngPadX = 10;
        const ngPadY = 4;
        const ngW = newGroupConfirmText.width + ngPadX * 2;
        const ngH = newGroupConfirmText.height + ngPadY * 2;
        newGroupConfirmBg.roundRect(0, 0, ngW, ngH, 3);
        newGroupConfirmBg.fill({ color: ACCENT });
        newGroupConfirmBtn.addChild(newGroupConfirmBg);
        newGroupConfirmText.x = ngPadX;
        newGroupConfirmText.y = ngPadY;
        newGroupConfirmBtn.addChild(newGroupConfirmText);
        newGroupConfirmBtn.hitArea = new Rectangle(0, 0, ngW, ngH);
        newGroupConfirmBtn.x = 0;
        newGroupConfirmBtn.y = dy;
        newGroupConfirmBtn.on("pointertap", (e) => {
          e.stopPropagation();
          const newGroupName = groupInputHandle ? groupInputHandle.value.trim() : "";
          if (newGroupName) {
            editingNewGroup = false;
            ctx.doc.change((draft) => {
              // add group if it doesn't already exist
              const groupExists = draft.groups.some((g: FriendGroup) => g.name === newGroupName);
              if (!groupExists) {
                draft.groups.push({ name: newGroupName, createdAt: new Date().toISOString() });
              }
              // assign friend to new group
              const idx = draft.friends.findIndex((f: FriendEntry) => f.id === friendIdForGroup);
              if (idx !== -1) draft.friends[idx].group = newGroupName;
            });
          }
        });
        detailInner.addChild(newGroupConfirmBtn);

        // cancel button
        const newGroupCancelBtn = new Container();
        newGroupCancelBtn.eventMode = "static";
        newGroupCancelBtn.cursor = "pointer";
        const newGroupCancelBg = new Graphics();
        newGroupCancelBg.eventMode = "none";
        const newGroupCancelText = new Text({
          text: "cancel",
          style: { fontFamily: FONT, fontSize: 9, fill: MUTED_TEXT },
          resolution: RESOLUTION,
        });
        newGroupCancelText.eventMode = "none";
        const ngcPadX = 10;
        const ngcPadY = 4;
        const ngcW = newGroupCancelText.width + ngcPadX * 2;
        const ngcH = newGroupCancelText.height + ngcPadY * 2;
        newGroupCancelBg.roundRect(0, 0, ngcW, ngcH, 3);
        newGroupCancelBg.fill({ color: FIELD_BG });
        newGroupCancelBg.stroke({ color: FIELD_BORDER, width: 1 });
        newGroupCancelBtn.addChild(newGroupCancelBg);
        newGroupCancelText.x = ngcPadX;
        newGroupCancelText.y = ngcPadY;
        newGroupCancelBtn.addChild(newGroupCancelText);
        newGroupCancelBtn.hitArea = new Rectangle(0, 0, ngcW, ngcH);
        newGroupCancelBtn.x = ngW + 6;
        newGroupCancelBtn.y = dy;
        newGroupCancelBtn.on("pointertap", (e) => {
          e.stopPropagation();
          editingNewGroup = false;
          layout(currentWidth, currentHeight);
        });
        detailInner.addChild(newGroupCancelBtn);

        dy += ngH + 8;
      }
    }

    // -----------------------------------------------------------------------
    // added date
    // -----------------------------------------------------------------------

    if (friend.createdAt) {
      const addedLabel = new Text({
        text: "added",
        style: { fontFamily: FONT, fontSize: LABEL_SIZE, fill: LABEL_COLOR },
        resolution: RESOLUTION,
      });
      addedLabel.eventMode = "none";
      addedLabel.y = dy;
      detailInner.addChild(addedLabel);
      dy += LABEL_SIZE + 4;

      const addedText = new Text({
        text: new Date(friend.createdAt).toLocaleDateString(),
        style: { fontFamily: FONT, fontSize: DETAIL_NODEID_SIZE, fill: TEXT_COLOR },
        resolution: RESOLUTION,
      });
      addedText.eventMode = "none";
      addedText.y = dy;
      detailInner.addChild(addedText);
      dy += addedText.height + 10;
    }

    // -----------------------------------------------------------------------
    // delete button — anchored right after the friend's own fields, before
    // the scrollable canvas-bin section below (a real user-reported
    // ordering preference, 2026-07-03: "remove friend" used to come after
    // the bin instead of before it).
    // -----------------------------------------------------------------------

    const deleteBtnY = dy;
    dy += DETAIL_BTN_HEIGHT + 12;
    // baseline content height assuming the friend's canvas bin below turns
    // out to be empty (or never loads) — grown in place once the async
    // lookup below actually finds canvases to show, see that section's
    // doc comment for why.
    detailContentHeight = dy;
    const deleteBtn = new Container();
    deleteBtn.eventMode = "static";
    deleteBtn.cursor = "pointer";
    deleteBtn.hitArea = new Rectangle(0, 0, contentW, DETAIL_BTN_HEIGHT);
    deleteBtn.y = deleteBtnY;
    detailInner.addChild(deleteBtn);

    const deleteBg = new Graphics();
    deleteBg.eventMode = "none";
    deleteBg.roundRect(0, 0, contentW, DETAIL_BTN_HEIGHT, DETAIL_BTN_RADIUS);
    deleteBg.fill({ color: FIELD_BG });
    deleteBg.stroke({ color: 0x7f1d1d, width: 1 });
    deleteBtn.addChild(deleteBg);

    const deleteText = new Text({
      text: "remove friend",
      style: { fontFamily: FONT, fontSize: TEXT_SIZE, fill: REJECT_COLOR },
      resolution: RESOLUTION,
    });
    deleteText.eventMode = "none";
    deleteText.x = (contentW - deleteText.width) / 2;
    deleteText.y = (DETAIL_BTN_HEIGHT - TEXT_SIZE) / 2;
    deleteBtn.addChild(deleteText);

    let deleteConfirmPending = false;
    let deleteConfirmTimer: ReturnType<typeof setTimeout> | null = null;
    const friendId = friend.id;

    deleteBtn.on("pointertap", (e) => {
      e.stopPropagation();
      if (!deleteConfirmPending) {
        // first click — switch to confirm state
        deleteConfirmPending = true;
        deleteText.text = "confirm remove?";
        deleteText.style.fill = 0xffffff;
        deleteText.x = (contentW - deleteText.width) / 2;
        deleteBg.clear();
        deleteBg.roundRect(0, 0, contentW, DETAIL_BTN_HEIGHT, DETAIL_BTN_RADIUS);
        deleteBg.fill({ color: REJECT_COLOR });
        // auto-reset after 3 seconds
        deleteConfirmTimer = setTimeout(() => {
          if (detailInner.destroyed) return;
          deleteConfirmPending = false;
          deleteText.text = "remove friend";
          deleteText.style.fill = REJECT_COLOR;
          deleteText.x = (contentW - deleteText.width) / 2;
          deleteBg.clear();
          deleteBg.roundRect(0, 0, contentW, DETAIL_BTN_HEIGHT, DETAIL_BTN_RADIUS);
          deleteBg.fill({ color: FIELD_BG });
          deleteBg.stroke({ color: 0x7f1d1d, width: 1 });
          deleteConfirmTimer = null;
        }, 3000);
      } else {
        // second click — actually remove
        if (deleteConfirmTimer) clearTimeout(deleteConfirmTimer);
        selectedFriendId = null;
        viewMode = "list";
        editingAlias = false;
        editingNewGroup = false;
        scrollY = 0;
        ctx.doc.change((draft) => {
          const idx = draft.friends.findIndex((f: FriendEntry) => f.id === friendId);
          if (idx !== -1) {
            draft.friends.splice(idx, 1);
          }
        });
      }
    });

    // -----------------------------------------------------------------------
    // friend's shared canvas bin — read-only, paginated (see canvas-bin.ts's
    // module doc comment / docs/hub-and-profile-plan.md). best-effort only:
    // shown once the friend's profile doc (already known via the gossip-
    // relayed nodeIds[].profileDocId, see friendz-wiring.ts's
    // mergeGossipDigestProfiles()) is reachable AND has published a
    // canvasBinDocId — no error UI for the "not there yet" cases, matching
    // this file's existing best-effort conventions elsewhere (bio, hub node
    // id, etc). deliberately last (after "remove friend", a real
    // user-reported ordering preference, 2026-07-03), and deliberately
    // does NOT reserve scroll space up front — only once the async lookup
    // confirms the friend actually has canvases to show does this grow
    // `detailContentHeight`, so an empty/not-yet-loaded bin never makes an
    // otherwise-short profile scrollable (a real user-reported request,
    // 2026-07-03: "if there's no canvases to show, the container shouldn't
    // scroll").
    // -----------------------------------------------------------------------
    {
      const mountY = detailContentHeight;
      const repo = ctx.canvasStore?.repo;
      const friendProfileDocId = friend.nodeIds[0]?.profileDocId;
      const requestFriendId = friend.id;
      if (repo && friendProfileDocId) {
        ProfileStore.open(repo, friendProfileDocId as DocumentId)
          .then(async (friendProfileStore) => {
            if (selectedFriendId !== requestFriendId || viewMode !== "detail") return;
            const canvasBinDocId = friendProfileStore.canvasBinDocId();
            if (!canvasBinDocId) return;
            const friendCanvasBinStore = await CanvasBinStore.open(repo, canvasBinDocId as DocumentId);
            if (selectedFriendId !== requestFriendId || viewMode !== "detail") return;
            // nothing to show — leave detailContentHeight at its baseline
            // (right after "remove friend") so the view doesn't scroll.
            if (friendCanvasBinStore.nodes().length === 0) return;
            if (friendCanvasBinController) {
              friendCanvasBinController.destroy();
              friendCanvasBinController = null;
            }
            const controller = createProfileCanvasBinWidget({
              canvasBinStore: friendCanvasBinStore,
              profileStore: friendProfileStore,
              width: contentW,
              height: PROFILE_CANVAS_BIN_HEIGHT,
              isReadOnly: true,
              registerTestHooks: (hooks) => registerSocialBridge({ friendCanvasBin: hooks }),
            });
            friendCanvasBinController = controller;
            controller.container.y = mountY;
            detailInner.addChild(controller.container);
            detailContentHeight = mountY + PROFILE_CANVAS_BIN_HEIGHT + 12;
          })
          .catch(() => {
            // friend's profile/canvas-bin doc not reachable yet — best
            // effort, no error UI (matches this file's existing convention).
          });
      }
    }
  };

  // ---------------------------------------------------------------------------
  // add-friend view
  // ---------------------------------------------------------------------------

  const addModeContainer = new Container();
  addModeContainer.eventMode = "static";
  addModeContainer.visible = false;
  container.addChild(addModeContainer);

  interface AddFieldEntry {
    label: Text;
    handle: SkeinInputHandle;
    layoutAt: (x: number, y: number, w: number) => void;
  }

  function createAddField(
    labelStr: string,
    parentContainer: Container,
    placeholder: string
  ): AddFieldEntry {
    const label = new Text({
      text: labelStr,
      style: { fontFamily: FONT, fontSize: LABEL_SIZE, fill: LABEL_COLOR },
      resolution: RESOLUTION,
    });
    label.eventMode = "none";
    parentContainer.addChild(label);

    const handle = createSkeinInput({
      canvasElement: ctx.canvasElement,
      width: 200, // initial width — updated by layoutAt
      height: FIELD_HEIGHT,
      placeholder,
      value: "",
      onChange: () => {},
    });

    parentContainer.addChild(handle.input);

    const layoutAt = (x: number, y: number, w: number) => {
      label.x = x;
      label.y = y;
      handle.input.x = x;
      handle.input.y = y + LABEL_SIZE + 4;
      handle.setWidth(w);
    };

    return { label, handle, layoutAt };
  }

  const nameField = createAddField(
    "alias (optional)",
    addModeContainer,
    "nickname for this friend..."
  );
  const nodeIdField = createAddField("node id", addModeContainer, "64-char hex node ID");

  // add-mode buttons: cancel and add
  const addCancelBtn = new Container();
  addCancelBtn.eventMode = "static";
  addCancelBtn.cursor = "pointer";

  const addCancelBg = new Graphics();
  addCancelBtn.addChild(addCancelBg);

  const addCancelText = new Text({
    text: "cancel",
    style: { fontFamily: FONT, fontSize: TEXT_SIZE, fill: MUTED_TEXT },
    resolution: RESOLUTION,
  });
  addCancelText.eventMode = "none";
  addCancelBtn.addChild(addCancelText);
  addModeContainer.addChild(addCancelBtn);

  addCancelBtn.on("pointertap", (e) => {
    e.stopPropagation();
    nameField.handle.blur();
    nodeIdField.handle.blur();
    viewMode = "list";
    addModeContainer.visible = false;
    nameField.handle.value = "";
    nodeIdField.handle.value = "";
    layout(currentWidth, currentHeight);
  });

  const addConfirmBtn = new Container();
  addConfirmBtn.eventMode = "static";
  addConfirmBtn.cursor = "pointer";

  const addConfirmBg = new Graphics();
  addConfirmBtn.addChild(addConfirmBg);

  const addConfirmText = new Text({
    text: "add",
    style: { fontFamily: FONT, fontSize: TEXT_SIZE, fontWeight: "bold", fill: 0xffffff },
    resolution: RESOLUTION,
  });
  addConfirmText.eventMode = "none";
  addConfirmBtn.addChild(addConfirmText);
  addModeContainer.addChild(addConfirmBtn);

  addConfirmBtn.on("pointertap", (e) => {
    e.stopPropagation();
    nameField.handle.blur();
    nodeIdField.handle.blur();

    const name = nameField.handle.value.trim();
    const nodeId = nodeIdField.handle.value.trim();

    if (!name && !nodeId) {
      // nothing to add — just close
      viewMode = "list";
      addModeContainer.visible = false;
      nameField.handle.value = "";
      nodeIdField.handle.value = "";
      layout(currentWidth, currentHeight);
      return;
    }

    // validate node ID format if provided
    if (nodeId && !isValidNodeId(nodeId)) {
      log.warn("social.friends", "invalid node ID format — expected 64-char hex string");
      return;
    }

    // switch view state BEFORE the doc change so that the on("change")
    // listener's layout() call renders the list view (not the add view)
    viewMode = "list";
    addModeContainer.visible = false;
    nameField.handle.value = "";
    nodeIdField.handle.value = "";
    scrollY = Infinity;

    ctx.doc.change((draft) => {
      draft.friends.push({
        id: crypto.randomUUID(),
        alias: name,
        username: "",
        group: "",
        nodeIds: nodeId
          ? [
              {
                nodeId: nodeId,
                addedAt: new Date().toISOString(),
                lastSeenAt: "",
                username: "",
                bio: "",
                avatarDataUrl: "",
                profileDocId: "",
                profileUpdatedAt: "",
              },
            ]
          : [],
        createdAt: new Date().toISOString(),
        // a manually-added friend is never a hub — hub status only ever
        // arrives via the isHub flag on a friend-request/friend-accept
        // message (see friendz-wiring.ts's wireFriendHandlers()).
        isHub: false,
      });
    });

    // if a node ID was provided and the protocol is ready, also send
    // a friend request so the remote peer gets notified
    if (nodeId) {
      const ready = bridgeIsProtocolReady();
      log.debug(
        "social.friends",
        "add-friend submit nodeId=" + nodeId.slice(0, 16) + "... protocolReady=" + ready
      );
      if (ready) {
        sendFriendRequest(nodeId)
          .then(() => {
            log.debug(
              "social.friends",
              "sendFriendRequest resolved for " + nodeId.slice(0, 16) + "..."
            );
          })
          .catch((err) => {
            log.warn("social.friends", "failed to send friend request after add:", err);
          });
      } else {
        log.warn(
          "social.friends",
          "skipped sendFriendRequest — friendz protocol bridge not ready yet"
        );
      }
    }
  });

  // ---------------------------------------------------------------------------
  // "add friend" button (shown in list view)
  // ---------------------------------------------------------------------------

  const addBtn = new Container();
  addBtn.eventMode = "static";
  addBtn.cursor = "pointer";

  const addBtnBg = new Graphics();
  addBtn.addChild(addBtnBg);

  const addBtnText = new Text({
    text: "add friend",
    style: { fontFamily: FONT, fontSize: TEXT_SIZE, fontWeight: "bold", fill: 0xffffff },
    resolution: RESOLUTION,
  });
  addBtnText.eventMode = "none";
  addBtn.addChild(addBtnText);
  container.addChild(addBtn);

  addBtn.on("pointertap", (e) => {
    e.stopPropagation();
    viewMode = "add";
    addModeContainer.visible = true;
    addBtn.visible = false;
    layout(currentWidth, currentHeight);
    // auto-focus the node id field (first field in the view)
    nodeIdField.handle.focus();
  });

  // ---------------------------------------------------------------------------
  // layout — called by parent with content area dimensions
  // ---------------------------------------------------------------------------

  const layout = (w: number, h: number) => {
    currentWidth = w;
    currentHeight = h;

    const friends = ctx.doc.current.friends;

    // hide all sub-view containers first
    listContainer.visible = false;
    detailContainer.visible = false;
    hubProfileContainer.visible = false;
    addModeContainer.visible = false;
    addBtn.visible = false;
    emptyText.visible = false;
    dropZoneBar.visible = false;

    // leaving the detail view — forget which friend we already sent a
    // live profile-refresh request for, so re-opening a detail view later
    // (same friend or not) fetches fresh again.
    if (viewMode !== "detail") {
      profileRefreshRequestedFor = null;
      presenceProbeRequestedFor = null;
    }

    switch (viewMode) {
      case "list": {
        listContainer.visible = true;
        addBtn.visible = true;

        const addBtnY = h - BUTTON_HEIGHT;

        addBtnBg.clear();
        addBtnBg.roundRect(0, 0, w, BUTTON_HEIGHT, BUTTON_RADIUS);
        addBtnBg.fill({ color: ACCENT });
        addBtn.hitArea = new Rectangle(0, 0, w, BUTTON_HEIGHT);
        addBtn.x = 0;
        addBtn.y = addBtnY;
        addBtnText.x = (w - addBtnText.width) / 2;
        addBtnText.y = (BUTTON_HEIGHT - TEXT_SIZE) / 2;

        // list area spans from top to above the add button
        const listTop = 0;
        listAreaHeight = addBtnY - listTop - 8;

        // update mask — adjust for drop zone bar during drag
        if (dragState?.isDragging) {
          layoutDropZones(w, listTop);
          dropZoneBar.visible = true;
          dzRemoveBg.visible = dragState.friendGroup !== "";
          dzRemoveLabel.visible = dragState.friendGroup !== "";
          const dzOffset = DROP_ZONE_HEIGHT + 4;
          listContainer.y = listTop + dzOffset;
          listMask.clear();
          listMask.rect(0, listTop + dzOffset, w, listAreaHeight - dzOffset);
          listMask.fill({ color: 0xffffff });
          listContainer.hitArea = new Rectangle(0, 0, w, listAreaHeight - dzOffset);
          listAreaHeight -= dzOffset;
        } else {
          // normal mask
          listMask.clear();
          listMask.rect(0, listTop, w, listAreaHeight);
          listMask.fill({ color: 0xffffff });
          listContainer.x = 0;
          listContainer.y = listTop;
          listContainer.hitArea = new Rectangle(0, 0, w, listAreaHeight);
        }

        // rebuild rows
        rebuildRows(friends, w);

        // clamp scroll after rebuilding
        clampScroll();
        positionListInner();

        // empty state
        if (friends.length === 0) {
          emptyText.visible = true;
          emptyText.text = "no friends yet";
          emptyText.x = (w - emptyText.width) / 2;
          emptyText.y = listTop + listAreaHeight / 2 - 6;
        }
        break;
      }

      case "detail": {
        const selectedFriend = friends.find((f) => f.id === selectedFriendId);
        if (!selectedFriend) {
          // friend was deleted or not found — go back to list
          viewMode = "list";
          selectedFriendId = null;
          editingAlias = false;
          editingNewGroup = false;
          layout(w, h);
          return;
        }
        detailContainer.visible = true;
        detailContainer.x = 0;
        detailContainer.y = 0;
        detailAreaHeight = h;
        detailMask.clear();
        detailMask.rect(0, 0, w, h).fill({ color: 0xffffff });
        detailContainer.hitArea = new Rectangle(0, 0, w, h);
        rebuildDetailView(selectedFriend, w);
        // clamp scroll now that rebuildDetailView() has set the baseline
        // content height (`detailContentHeight`) — the friend-canvas-bin
        // section only grows it further, asynchronously, if/when it turns
        // out the friend actually has canvases to show (see that
        // section's doc comment); `detailContentHeight` is read live by
        // `clampDetailScroll()`/the wheel handler, so no re-clamp call is
        // needed once that async lookup resolves.
        clampDetailScroll();
        break;
      }

      case "hubProfile": {
        const selectedFriend = friends.find((f) => f.id === selectedFriendId);
        const hubNodeId = selectedFriend?.nodeIds[0]?.nodeId;
        if (!selectedFriend?.isHub || !hubNodeId) {
          // friend deleted, no longer a hub, or has no node id — bail back
          // to the friend-detail view (or list, if the friend is gone too).
          viewMode = selectedFriend ? "detail" : "list";
          layout(w, h);
          return;
        }
        hubProfileContainer.visible = true;
        hubProfileContainer.x = 0;
        hubProfileContainer.y = 0;
        mountOrLayoutHubProfilePanel(hubNodeId, w, h);
        break;
      }

      case "add": {
        addModeContainer.visible = true;
        addModeContainer.x = 0;
        addModeContainer.y = 0;

        let addY = 0;

        // small internal padding for the input fields
        const fieldPad = PADDING_X;
        const fieldW = w - fieldPad * 2;

        // node id field — listed first, auto-focused when the view opens
        nodeIdField.layoutAt(fieldPad, addY, fieldW);
        addY += LABEL_SIZE + 4 + FIELD_HEIGHT + FIELD_GAP;

        // alias field (optional)
        nameField.layoutAt(fieldPad, addY, fieldW);
        addY += LABEL_SIZE + 4 + FIELD_HEIGHT + FIELD_GAP;

        // buttons — anchored to the bottom
        const buttonY = h - BUTTON_HEIGHT;
        const buttonW = (w - BUTTON_GAP) / 2;

        // cancel button
        addCancelBg.clear();
        addCancelBg.roundRect(0, 0, buttonW, BUTTON_HEIGHT, BUTTON_RADIUS);
        addCancelBg.fill({ color: FIELD_BG });
        addCancelBg.stroke({ color: FIELD_BORDER, width: 1 });
        addCancelBtn.hitArea = new Rectangle(0, 0, buttonW, BUTTON_HEIGHT);
        addCancelBtn.x = 0;
        addCancelBtn.y = buttonY;
        addCancelText.x = (buttonW - addCancelText.width) / 2;
        addCancelText.y = (BUTTON_HEIGHT - TEXT_SIZE) / 2;

        // add button
        addConfirmBg.clear();
        addConfirmBg.roundRect(0, 0, buttonW, BUTTON_HEIGHT, BUTTON_RADIUS);
        addConfirmBg.fill({ color: ACCENT });
        addConfirmBtn.hitArea = new Rectangle(0, 0, buttonW, BUTTON_HEIGHT);
        addConfirmBtn.x = buttonW + BUTTON_GAP;
        addConfirmBtn.y = buttonY;
        addConfirmText.x = (buttonW - addConfirmText.width) / 2;
        addConfirmText.y = (BUTTON_HEIGHT - TEXT_SIZE) / 2;
        break;
      }
    }
  };

  // ---------------------------------------------------------------------------
  // subscriptions
  // ---------------------------------------------------------------------------

  // subscribe to doc changes for re-rendering
  const unsub = ctx.doc.on("change", () => {
    layout(currentWidth, currentHeight);
  });

  // subscribe to online status changes for re-rendering friend dots and detail view
  const unsubOnline = onOnlineChange(() => {
    if (viewMode === "list" || viewMode === "detail") {
      layout(currentWidth, currentHeight);
    }
  });

  // ---------------------------------------------------------------------------
  // test hooks — docs/hub-and-profile-plan.md section 5/8 step 7. registered
  // via registerSocialBridge(), same pattern profile-tab.ts already uses for
  // pickAvatar. no-ops (never registered) outside DEV builds.
  // ---------------------------------------------------------------------------

  registerSocialBridge({
    friendsTab: {
      getViewMode: () => viewMode,
      openFriendDetail: (friendId: string) => {
        selectedFriendId = friendId;
        viewMode = "detail";
        editingAlias = false;
        editingNewGroup = false;
        layout(currentWidth, currentHeight);
      },
      closeFriendDetail: () => {
        selectedFriendId = null;
        viewMode = "list";
        editingAlias = false;
        editingNewGroup = false;
        layout(currentWidth, currentHeight);
      },
      hasManageHubAction: () => {
        if (viewMode !== "detail" || !selectedFriendId) return false;
        const friend = ctx.doc.current.friends.find((f: FriendEntry) => f.id === selectedFriendId);
        return Boolean(friend?.isHub);
      },
      openHubProfilePanel: () => {
        if (viewMode !== "detail" || !selectedFriendId) return;
        const friend = ctx.doc.current.friends.find((f: FriendEntry) => f.id === selectedFriendId);
        if (!friend?.isHub) return;
        viewMode = "hubProfile";
        layout(currentWidth, currentHeight);
      },
      closeHubProfilePanel: () => {
        closeHubProfilePanelView();
      },
      isHubProfilePanelOpen: () => viewMode === "hubProfile" && hubProfileHandle !== null,
      getHubProfilePanelState: () => hubProfileHandle?.getState() ?? null,
      refreshHubProfilePanel: () => hubProfileHandle?.refresh() ?? Promise.resolve(),
      getHubProfileAllowInputGlobalPos: () => hubProfileHandle?.getAllowInputGlobalPos() ?? null,
      getHubProfileAllowButtonGlobalPos: () => hubProfileHandle?.getAllowButtonGlobalPos() ?? null,
      getHubProfileRemoveButtonGlobalPos: (nodeId: string) =>
        hubProfileHandle?.getRemoveButtonGlobalPos(nodeId) ?? null,
      getFriendDetailNodeIdText: () => detailNodeIdTextRef?.text ?? null,
      getHubProfileBackButtonGlobalPos: () => {
        const p = hubProfileBackBtn.getGlobalPosition();
        return { x: p.x + hubProfileBackBtn.width / 2, y: p.y + hubProfileBackBtn.height / 2 };
      },
      getHubProfileBlobPageState: () => hubProfileHandle?.getBlobPageState() ?? null,
      getHubProfileSoftDeletedPageState: () => hubProfileHandle?.getSoftDeletedPageState() ?? null,
      getHubProfileCanvasPageState: () => hubProfileHandle?.getCanvasPageState() ?? null,
      getHubProfileBlobPrevButtonGlobalPos: () => hubProfileHandle?.getBlobPrevButtonGlobalPos() ?? null,
      getHubProfileBlobNextButtonGlobalPos: () => hubProfileHandle?.getBlobNextButtonGlobalPos() ?? null,
      getHubProfileSoftDeletedPrevButtonGlobalPos: () => hubProfileHandle?.getSoftDeletedPrevButtonGlobalPos() ?? null,
      getHubProfileSoftDeletedNextButtonGlobalPos: () => hubProfileHandle?.getSoftDeletedNextButtonGlobalPos() ?? null,
      getHubProfileCanvasPrevButtonGlobalPos: () => hubProfileHandle?.getCanvasPrevButtonGlobalPos() ?? null,
      getHubProfileCanvasNextButtonGlobalPos: () => hubProfileHandle?.getCanvasNextButtonGlobalPos() ?? null,
      getHubProfileUnsyncButtonGlobalPos: (canvasDocId: string) =>
        hubProfileHandle?.getUnsyncButtonGlobalPos(canvasDocId) ?? null,
      getHubProfileScrollState: () => hubProfileHandle?.getScrollState() ?? null,
      getHubProfilePanelGlobalPos: () => hubProfileHandle?.getPanelGlobalPos() ?? null,
      getHubProfileSectionCollapsed: (sectionId: string) =>
        hubProfileHandle?.getSectionCollapsed(sectionId) ?? false,
      toggleHubProfileSection: (sectionId: string) =>
        hubProfileHandle?.toggleSection(sectionId),
      getHubProfileProfileState: () => hubProfileHandle?.getHubProfileState() ?? null,
      getHubProfileCanvasBlobsState: (canvasDocId: string) =>
        hubProfileHandle?.getCanvasBlobsState(canvasDocId) ?? null,
    },
  });

  // ---------------------------------------------------------------------------
  // controller
  // ---------------------------------------------------------------------------

  return {
    container,

    layout(width: number, height: number) {
      layout(width, height);
    },

    destroy() {
      nameField.handle.destroy();
      nodeIdField.handle.destroy();
      if (aliasInputHandle) {
        aliasInputHandle.destroy();
        aliasInputHandle = null;
      }
      if (groupInputHandle) {
        groupInputHandle.destroy();
        groupInputHandle = null;
      }
      if (groupRenameHandle) {
        groupRenameHandle.destroy();
        groupRenameHandle = null;
      }
      if (hubProfileHandle) {
        hubProfileHandle.destroy();
        hubProfileHandle = null;
      }
      if (dragState) {
        cancelDrag();
      }
      window.removeEventListener("pointerup", globalRelease, true);
      window.removeEventListener("mouseup", globalRelease, true);
      window.removeEventListener("touchend", globalRelease, true);
      window.removeEventListener("pointercancel", globalCancel, true);
      window.removeEventListener("touchcancel", globalCancel, true);
      unsub();
      unsubOnline();
      container.destroy({ children: true });
    },
  };
}
